import {
  getHouseholdCatalogs,
  getMemberHouseholds,
  saveMemberHousehold,
  uploadHouseholdMemberPhoto,
  type HouseholdCatalogsResponse,
  type HouseholdContract,
  type SaveHouseholdRequest,
} from '@urbanik/api-client';
import { AvatarUpload, CanonicalModal } from '@urbanik/ui';
import { ArrowDown, ArrowUp, History, Pencil, Trash2, UserPlus, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import { HouseholdMemberPhotoField } from './HouseholdMemberPhotoField';
type DraftMember = SaveHouseholdRequest['members'][number] & { photoFile: File | null };
type Draft = {
  householdId: string | null;
  expectedVersion: number | null;
  periodStart: string;
  periodEnd: string;
  includesMember: boolean;
  members: DraftMember[];
  reason: string;
};
const emptyMember = (): DraftMember => ({
  photoFile: null,
  id: null,
  givenNames: '',
  familyNames: '',
  relationshipCode: '',
  sexCode: null,
  birthDate: null,
  civilStatusCode: null,
  educationLevelCode: null,
  isCurrentlyStudying: null,
  educationInstitutionTypeCode: null,
  isPrimaryNucleus: true,
});
const emptyDraft = (): Draft => ({
  householdId: null,
  expectedVersion: null,
  periodStart: `${new Date().getFullYear()}-01-01`,
  periodEnd: '',
  includesMember: true,
  members: [],
  reason: '',
});
export function MemberHousehold({
  memberId,
  canManage,
  canReadPhotos,
  canManagePhotos,
  embedded = false,
}: {
  memberId: string;
  canManage: boolean;
  canReadPhotos: boolean;
  canManagePhotos: boolean;
  embedded?: boolean;
}) {
  const [data, setData] = useState<HouseholdContract[]>([]),
    [catalogs, setCatalogs] = useState<HouseholdCatalogsResponse>({ catalogs: [] }),
    [draft, setDraft] = useState<Draft>(emptyDraft()),
    [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const load = () =>
    Promise.all([getMemberHouseholds(apiBase, memberId), getHouseholdCatalogs(apiBase)])
      .then(([h, c]) => {
        setData(h.households);
        setCatalogs(c);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'No pudimos cargar el hogar.'));
  useEffect(() => {
    void load();
  }, [memberId]);
  const current = data[0] ?? null;
  const optionLabel = (catalog: string, code: string | null) =>
    catalogs.catalogs.find((x) => x.code === catalog)?.options.find((x) => x.code === code)
      ?.label ?? 'No registrado';
  const begin = (h: HouseholdContract | null) => {
    setDraft(
      h
        ? {
            householdId: h.id,
            expectedVersion: h.version,
            periodStart: h.periodStart,
            periodEnd: h.periodEnd ?? '',
            includesMember: h.includesMember,
            members: h.members.map((m) => ({ ...m, id: m.id, photoFile: null })),
            reason: '',
          }
        : emptyDraft(),
    );
    setError('');
    setOpen(true);
  };
  const updateMember = (index: number, values: Partial<DraftMember>) =>
    setDraft({
      ...draft,
      members: draft.members.map((m, i) => (i === index ? { ...m, ...values } : m)),
    });
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= draft.members.length) return;
    const members = [...draft.members];
    [members[index], members[target]] = [members[target]!, members[index]!];
    setDraft({ ...draft, members });
  };
  const cat = (code: string) => catalogs.catalogs.find((x) => x.code === code)?.options ?? [];
  const select = (index: number, key: keyof DraftMember, label: string, catalog: string) => (
    <label>
      {label}
      <select
        className="ui-select"
        value={String(draft.members[index]![key] ?? '')}
        onChange={(e) => updateMember(index, { [key]: e.target.value || null })}
      >
        <option value="">No registrado</option>
        {cat(catalog).map((o) => (
          <option key={o.code} value={o.code}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const members = draft.members.map((draftMember) => {
        const { photoFile, ...member } = draftMember;
        void photoFile;
        return member;
      });
      const r = await saveMemberHousehold(apiBase, memberId, {
        householdId: draft.householdId,
        expectedVersion: draft.expectedVersion,
        periodStart: draft.periodStart,
        periodEnd: draft.periodEnd || null,
        includesMember: draft.includesMember,
        members,
        reason: draft.reason,
      });
      setData(r.households);
      const saved =
        r.households.find((household) => household.id === draft.householdId) ?? r.households[0];
      const pending = draft.members
        .map((member, index) => ({ member, index }))
        .filter((entry) => entry.member.photoFile);
      if (saved && pending.length) {
        await Promise.all(
          pending.map(async ({ member, index }) => {
            const persisted = saved.members[index];
            if (!persisted || !member.photoFile)
              throw new Error('El hogar se guardó, pero no pudimos asociar una fotografía.');
            await uploadHouseholdMemberPhoto(
              apiBase,
              memberId,
              saved.id,
              persisted.id,
              member.photoFile,
            );
          }),
        );
      }
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos guardar el hogar.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className={`member-profile-expanded member-household-card${embedded ? ' member-admin-component--embedded' : ''}`}
    >
      <div className="member-profile-section-heading">
        <div>
          <p className="kicker">Composición familiar</p>
          <h2>Hogar e integrantes</h2>
        </div>
        {canManage ? (
          <button
            className="ui-button ui-button--primary"
            onClick={() => begin(current)}
            aria-label={current ? 'Editar hogar' : 'Registrar hogar'}
          >
            {current ? <Pencil aria-hidden="true" /> : <UserPlus aria-hidden="true" />}
          </button>
        ) : null}
      </div>
      {error && !open ? (
        <p role="alert" className="member-status-error">
          {error}
        </p>
      ) : null}
      {current ? (
        <>
          <div className="member-household-stats">
            <span className="ui-status ui-status--ready">
              <Users aria-hidden="true" />
              {current.declaredPersonCount} personas
            </span>
            <span className="ui-status ui-status--review">
              {current.cohabitingFamilyCount} núcleo principal
            </span>
            <span className="ui-status">
              {current.periodStart} → {current.periodEnd ?? 'Vigente'}
            </span>
          </div>
          <div className="member-household-list">
            {current.members.length ? (
              current.members.map((m) => (
                <article key={m.id}>
                  <HouseholdMemberPhotoField
                    memberId={memberId}
                    householdId={current.id}
                    householdMemberId={m.id}
                    initials={`${m.givenNames[0] ?? ''}${m.familyNames[0] ?? ''}`}
                    isMinor={Boolean(
                      m.birthDate &&
                      Date.now() - Date.parse(`${m.birthDate}T00:00:00Z`) <
                        18 * 365.2425 * 24 * 60 * 60 * 1000,
                    )}
                    canRead={canReadPhotos}
                    canManage={canManagePhotos}
                  />
                  <div>
                    <strong>
                      {m.givenNames} {m.familyNames}
                    </strong>
                    <span>
                      {optionLabel('relationship', m.relationshipCode)} ·{' '}
                      {m.isPrimaryNucleus ? 'Núcleo principal' : 'Otro conviviente'}
                    </span>
                  </div>
                  <dl>
                    <div>
                      <dt>Nacimiento</dt>
                      <dd>{m.birthDate ?? 'No registrado'}</dd>
                    </div>
                    <div>
                      <dt>Sexo registrado</dt>
                      <dd>{optionLabel('recorded_sex', m.sexCode)}</dd>
                    </div>
                    <div>
                      <dt>Educación</dt>
                      <dd>{optionLabel('education_level', m.educationLevelCode)}</dd>
                    </div>
                  </dl>
                </article>
              ))
            ) : (
              <p>
                No se declararon integrantes adicionales; el hogar incluye únicamente al socio
                titular.
              </p>
            )}
          </div>
          <details className="member-location-history">
            <summary>
              <History aria-hidden="true" /> Historial de periodos ({data.length})
            </summary>
            <ul>
              {data.map((h) => (
                <li key={h.id}>
                  <strong>
                    {h.periodStart} → {h.periodEnd ?? 'Vigente'}
                  </strong>
                  <span>
                    {h.declaredPersonCount} personas · versión {h.version}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </>
      ) : (
        <div className="member-household-empty">
          <Users aria-hidden="true" />
          <p>No existe una declaración de hogar.</p>
          {canManage ? (
            <button className="ui-button ui-button--primary" onClick={() => begin(null)}>
              <UserPlus aria-hidden="true" /> Registrar hogar
            </button>
          ) : null}
        </div>
      )}
      <CanonicalModal
        open={open}
        size="large"
        eyebrow="Información personal"
        title={draft.householdId ? 'Actualizar grupo familiar' : 'Registrar grupo familiar'}
        description="Indica quiénes viven actualmente con el socio y desde cuándo comparten este hogar."
        onClose={() => {
          if (!busy) setOpen(false);
        }}
      >
        <form
          className="member-household-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <fieldset>
            <legend>Situación actual del hogar</legend>
            <div className="member-location-grid">
              <label>
                ¿Desde cuándo vive este grupo familiar junto? *
                <input
                  className="ui-input"
                  type="date"
                  required
                  value={draft.periodStart}
                  onChange={(e) => setDraft({ ...draft, periodStart: e.target.value })}
                />
              </label>
              {draft.householdId ? (
                <label>
                  ¿Hasta cuándo estuvo vigente?{' '}
                  <span className="member-household-optional">Opcional</span>
                  <input
                    className="ui-input"
                    type="date"
                    value={draft.periodEnd}
                    onChange={(e) => setDraft({ ...draft, periodEnd: e.target.value })}
                  />
                </label>
              ) : null}
            </div>
            <label className="member-household-check">
              <input
                type="checkbox"
                checked={draft.includesMember}
                onChange={(e) => setDraft({ ...draft, includesMember: e.target.checked })}
              />{' '}
              El socio vive actualmente en este hogar
            </label>
            <p className="member-household-help">
              La fecha final sólo se utiliza cuando esta composición familiar deja de estar vigente.
            </p>
          </fieldset>
          <div className="member-household-editor-heading">
            <div>
              <h3>Integrantes declarados</h3>
              <p>
                {draft.members.length + (draft.includesMember ? 1 : 0)} personas ·{' '}
                {draft.members.filter((x) => x.isPrimaryNucleus).length +
                  (draft.includesMember ? 1 : 0)}{' '}
                núcleo principal
              </p>
            </div>
            <button
              type="button"
              className="ui-button ui-button--primary"
              onClick={() => setDraft({ ...draft, members: [...draft.members, emptyMember()] })}
            >
              <UserPlus aria-hidden="true" /> Añadir integrante
            </button>
          </div>
          <div className="member-household-editor">
            {draft.members.map((m, index) => (
              <fieldset key={m.id ?? `new-${index}`}>
                <legend>Integrante {index + 1}</legend>
                <div className="member-household-member-actions">
                  <button
                    type="button"
                    className="ui-row-action"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label="Mover integrante hacia arriba"
                  >
                    <ArrowUp />
                  </button>
                  <button
                    type="button"
                    className="ui-row-action"
                    onClick={() => move(index, 1)}
                    disabled={index === draft.members.length - 1}
                    aria-label="Mover integrante hacia abajo"
                  >
                    <ArrowDown />
                  </button>
                  <button
                    type="button"
                    className="ui-row-action"
                    onClick={() =>
                      setDraft({ ...draft, members: draft.members.filter((_, i) => i !== index) })
                    }
                    aria-label="Retirar integrante"
                  >
                    <Trash2 />
                  </button>
                </div>
                {m.id && draft.householdId ? (
                  <HouseholdMemberPhotoField
                    memberId={memberId}
                    householdId={draft.householdId}
                    householdMemberId={m.id}
                    initials={`${m.givenNames[0] ?? ''}${m.familyNames[0] ?? ''}`}
                    isMinor={Boolean(
                      m.birthDate &&
                      Date.now() - Date.parse(`${m.birthDate}T00:00:00Z`) <
                        18 * 365.2425 * 24 * 60 * 60 * 1000,
                    )}
                    canRead={canReadPhotos}
                    canManage={canManagePhotos}
                  />
                ) : canReadPhotos ? (
                  <AvatarUpload
                    label="Fotografía del integrante"
                    fallbackText={`${m.givenNames[0] ?? ''}${m.familyNames[0] ?? ''}`}
                    file={m.photoFile}
                    readOnly={!canManagePhotos}
                    warning={
                      m.birthDate &&
                      Date.now() - Date.parse(`${m.birthDate}T00:00:00Z`) <
                        18 * 365.2425 * 24 * 60 * 60 * 1000
                        ? 'La persona es menor de edad. Confirma la autorización aplicable antes de seleccionar una imagen.'
                        : ''
                    }
                    helpText="La fotografía se cargará después de guardar el integrante."
                    onFileChange={(photoFile) => updateMember(index, { photoFile })}
                  />
                ) : null}
                <div className="member-location-grid">
                  <label>
                    Nombres *
                    <input
                      className="ui-input"
                      required
                      value={m.givenNames}
                      onChange={(e) => updateMember(index, { givenNames: e.target.value })}
                    />
                  </label>
                  <label>
                    Apellidos *
                    <input
                      className="ui-input"
                      required
                      value={m.familyNames}
                      onChange={(e) => updateMember(index, { familyNames: e.target.value })}
                    />
                  </label>
                  {select(index, 'relationshipCode', 'Parentesco *', 'relationship')}
                  {select(index, 'sexCode', 'Sexo registrado', 'recorded_sex')}
                  <label>
                    Fecha de nacimiento
                    <input
                      className="ui-input"
                      type="date"
                      value={m.birthDate ?? ''}
                      onChange={(e) => updateMember(index, { birthDate: e.target.value || null })}
                    />
                  </label>
                  {select(index, 'civilStatusCode', 'Estado civil', 'civil_status')}
                  {select(index, 'educationLevelCode', 'Nivel educativo', 'education_level')}
                  <label>
                    ¿Estudia actualmente?
                    <select
                      className="ui-select"
                      value={m.isCurrentlyStudying === null ? '' : String(m.isCurrentlyStudying)}
                      onChange={(e) =>
                        updateMember(index, {
                          isCurrentlyStudying:
                            e.target.value === '' ? null : e.target.value === 'true',
                          educationInstitutionTypeCode:
                            e.target.value === 'true' ? m.educationInstitutionTypeCode : null,
                        })
                      }
                    >
                      <option value="">No registrado</option>
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  </label>
                  {m.isCurrentlyStudying
                    ? select(
                        index,
                        'educationInstitutionTypeCode',
                        'Tipo de institución',
                        'education_institution_type',
                      )
                    : null}
                </div>
                <label className="member-household-check">
                  <input
                    type="checkbox"
                    checked={m.isPrimaryNucleus}
                    onChange={(e) => updateMember(index, { isPrimaryNucleus: e.target.checked })}
                  />{' '}
                  Pertenece al núcleo principal
                </label>
              </fieldset>
            ))}
          </div>
          <label className="member-household-reason">
            Motivo del registro o cambio *
            <textarea
              required
              minLength={10}
              maxLength={500}
              value={draft.reason}
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            />
          </label>
          {error ? (
            <p role="alert" className="member-status-error">
              {error}
            </p>
          ) : null}
          <div className="member-status-actions">
            <button
              type="button"
              className="ui-button"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancelar
            </button>
            <button className="ui-button ui-button--primary" disabled={busy}>
              {busy ? 'Guardando…' : 'Confirmar hogar'}
            </button>
          </div>
        </form>
      </CanonicalModal>
    </section>
  );
}
